import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import config from '../config';

const API_BASE_URL = config.API_URL;

axios.defaults.baseURL = API_BASE_URL;
axios.defaults.timeout = config.TIMEOUT;

try {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }
} catch (e) {}

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => {
    try {
      return typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    } catch (e) {
      return null;
    }
  });

  const [user, setUser] = useState(() => {
    try {
      const storedToken = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
      if (storedToken) {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          return JSON.parse(storedUser);
        }
      }
      return null;
    } catch (e) {
      return null;
    }
  });

  const [loading, setLoading] = useState(() => {
    try {
      return typeof window !== 'undefined' ? !!localStorage.getItem('token') : false;
    } catch (e) {
      return false;
    }
  });

  const login = async (credentials) => {
    try {
      const response = await axios.post('/auth/login', {
        username: credentials.username,
        password: credentials.password
      });
      
      if (response.data.success && response.data.data) {
        const { token: newToken, user: userData } = response.data.data;
        
        // Đảm bảo permissions và roles là array
        const normalizedUser = {
          ...userData,
          permissions: Array.isArray(userData.permissions) ? userData.permissions : [],
          roles: Array.isArray(userData.roles) ? userData.roles : (userData.role ? [userData.role] : [])
        };
        
        setToken(newToken);
        setUser(normalizedUser);
        localStorage.setItem('token', newToken);
        localStorage.setItem('user', JSON.stringify(normalizedUser));
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        
        return { success: true, user: normalizedUser };
      } else {
        throw new Error(response.data.message || 'Đăng nhập thất bại');
      }
    } catch (error) {
      if (error.message === 'Network Error' || error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') {
        return {
          success: false,
          message: 'Lỗi network'
        };
      }
      
      const errorMessage = error.response?.data?.message || error.message || 'Đăng nhập thất bại';
      return { success: false, message: errorMessage };
    }
  };

  const logout = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          await axios.post('/auth/logout');
        } catch (error) {
          // Silent fail
        }
      }
    } catch (error) {
      // Silent fail
    } finally {
      setUser(null);
      setToken(null);
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      } catch (e) {}
      delete axios.defaults.headers.common['Authorization'];
      return true;
    }
  }, []);

  const loadUserProfile = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await axios.get('/auth/profile');
      const userData = response.data.data.user || response.data.data;
      const newToken = response.data.data.token;
      
      // Đảm bảo permissions và roles là array
      const normalizedUser = {
        ...userData,
        permissions: Array.isArray(userData.permissions) ? userData.permissions : [],
        roles: Array.isArray(userData.roles) ? userData.roles : (userData.role ? [userData.role] : [])
      };
      
      setUser(normalizedUser);
      localStorage.setItem('user', JSON.stringify(normalizedUser));

      if (newToken) {
        setToken(newToken);
        localStorage.setItem('token', newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      }
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  const hasPermission = useCallback((permission) => {
    if (!user) return false;
    
    // Kiểm tra permission "all" = full access
    if (user.permissions?.includes('all')) {
      return true;
    }
    
    // Kiểm tra nếu có role admin hoặc super_admin (case-insensitive)
    const roles = user.roles || [];
    const hasAdminRole = roles.some(role => 
      role?.toLowerCase() === 'admin' || 
      role?.toLowerCase() === 'administrator' ||
      role?.toLowerCase() === 'super_admin'
    );
    
    if (hasAdminRole) {
      return true;
    }
    
    // Kiểm tra permission cụ thể (case-insensitive)
    if (!permission) return false;
    return user.permissions?.some(p => 
      p?.toLowerCase() === permission.toLowerCase()
    ) || false;
  }, [user]);

  const isAdmin = useCallback(() => {
    if (!user) return false;
    
    // Kiểm tra permission "all" = admin
    if (user.permissions?.includes('all')) {
      return true;
    }
    
    // Kiểm tra roles (case-insensitive)
    const roles = user.roles || [];
    return roles.some(role => 
      role?.toLowerCase() === 'admin' 
    );
  }, [user]);

  const hasRole = useCallback((roleName) => {
    if (!user) return false;
    
    if (isAdmin()) {
      return true;
    }
    
    const roles = user.roles || [];
    if (roles.length > 0) {
      return roles.some(role => 
        role?.toLowerCase() === roleName?.toLowerCase()
      );
    }
    
    // Backward compatibility: check user.role
    return user.role?.toLowerCase() === roleName?.toLowerCase();
  }, [user, isAdmin]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      loadUserProfile();
    } else {
      setLoading(false);
    }
  }, [token, loadUserProfile]);

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    hasPermission,
    isAdmin,
    hasRole,
    loadUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;